import { Link } from 'react-router-dom';
import { addDays, todayIn } from '@njpos/core';
import { api, dayLabel, usd, usd0 } from '../lib/api';
import { ErrorNote, useLoad } from '../components/ui';

const LINK: Record<string, string> = { bill_due: '/bills', rip_overdue: '/bills', invoices_waiting: '/invoices', deals_ready: '/buy', deals_ending: '/buy', below_cost_price: '/items' };

export default function Today() {
  const today = todayIn();
  const alerts = useLoad(() => api<any[]>('/api/alerts'), []);
  const sales = useLoad(() => api<any>(`/api/reports/sales?from=${today}&to=${today}`), []);
  const week = useLoad(() => api<any>(`/api/reports/sales?from=${addDays(today, -6)}&to=${today}`), []);
  const bills = useLoad(() => api<any[]>('/api/bills?status=open'), []);
  const owed = (bills.data ?? []).reduce((s, b) => s + b.balance_cents, 0);
  // From the 18th (when next month's CPL is public), warn about shelf prices it makes illegal or below cost.
  const prices = useLoad(() => (Number(today.slice(8, 10)) >= 18 ? api<any>('/api/checks/prices') : Promise.resolve(null)), []);
  const priceProblems = prices.data ? prices.data.counts.illegal + prices.data.counts.below_frontline : 0;

  return (
    <div className="stack">
      <div className="page-head"><div className="grow"><h1>Today</h1><p>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p></div></div>
      <div className="stats">
        <div><div className="v">{usd0(sales.data?.net_sales_cents)}</div><div className="l">Sales today, before tax</div></div>
        <div><div className="v">{sales.data?.sales ?? '—'}</div><div className="l">Transactions today</div></div>
        <div><div className="v">{usd0(week.data?.gross_margin_cents)}</div><div className="l">Gross margin, last 7 days</div></div>
        <div><div className="v">{usd0(owed)}</div><div className="l">Owed to vendors</div></div>
      </div>
      {priceProblems > 0 && (
        <div className={`notice ${prices.data.counts.illegal ? 'bad' : ''}`} role="status">
          <b>{priceProblems} shelf price{priceProblems === 1 ? '' : 's'} need{priceProblems === 1 ? 's' : ''} fixing before {dayLabel(prices.data.starts)}</b>
          {' '}({prices.data.days_until} days):{' '}
          {prices.data.counts.illegal > 0 && `${prices.data.counts.illegal} would be illegal under the new CPL`}{prices.data.counts.illegal > 0 && prices.data.counts.below_frontline > 0 && ', '}
          {prices.data.counts.below_frontline > 0 && `${prices.data.counts.below_frontline} below frontline cost`}. <Link to="/checks?tab=prices">See the prices</Link>
        </div>
      )}
      <div className="panel">
        <div className="panel-head"><h2 className="grow">Needs attention</h2></div>
        <ErrorNote error={alerts.error} />
        {alerts.data?.length === 0 && <div className="empty">Nothing needs you right now.</div>}
        <div>
          {alerts.data?.map((a) => (
            <div key={a.id} className="row" style={{ padding: '12px 16px', borderBottom: '1px solid var(--line-2)' }}>
              <span className={`chip ${a.severity === 'critical' ? 'bad' : a.severity === 'warning' ? 'warn' : ''}`}>{a.severity === 'critical' ? 'Act now' : a.severity === 'warning' ? 'Soon' : 'FYI'}</span>
              <span className="grow">{a.message}</span>
              {LINK[a.kind] && <Link to={LINK[a.kind]!}>Open</Link>}
              <button className="ghost" onClick={async () => { await api(`/api/alerts/${a.id}/dismiss`, { method: 'POST' }); alerts.reload(); }}>Dismiss</button>
            </div>
          ))}
        </div>
      </div>
      {sales.data?.topItems?.length > 0 && (
        <div className="panel">
          <div className="panel-head"><h2>Selling today</h2></div>
          <table className="grid"><thead><tr><th>Product</th><th className="n">Units</th><th className="n">Sales</th></tr></thead>
            <tbody>{sales.data.topItems.slice(0, 8).map((t: any) => <tr key={`${t.brand}${t.name}${t.size_ml}`}><td>{t.brand} {t.name} <span className="faint">{t.size_ml}ml</span></td><td className="n">{t.units}</td><td className="n">{usd(t.net_cents)}</td></tr>)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
